import { forwardRef, useRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import * as Tooltip from '@radix-ui/react-tooltip'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { sizeClass, type DsSize } from '@/ds/tone'
import { DsSpinner } from '@/ds/spinner'
import { DsIconGlyph } from '@/ds/icon'
import type { DsIcon } from '@/ds/icons'

export type DsButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive' | 'success'

const buttonVariants = cva(
  'relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-sans font-semibold outline-none transition-[transform,opacity,background-color,box-shadow] motion-fast focus-visible:ring-focus active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-[linear-gradient(135deg,#059669,#0d9488)] text-white shadow-glow-brand hover:brightness-105',
        secondary: 'bg-secondary text-secondary-foreground hover:brightness-95 dark:hover:brightness-110',
        outline: 'border border-input bg-transparent text-foreground hover:bg-muted',
        ghost: 'bg-transparent text-foreground hover:bg-muted',
        link: 'h-auto min-h-tap !px-1 bg-transparent text-link underline-offset-4 hover:underline',
        destructive: 'bg-danger text-danger-foreground hover:brightness-95',
        success: 'bg-success text-success-foreground hover:brightness-95',
      },
    },
    defaultVariants: { variant: 'primary' },
  },
)

export interface DsButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'> {
  label?: ReactNode
  children?: ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => unknown
  variant?: DsButtonVariant
  size?: DsSize
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  leadingIcon?: DsIcon
  trailingIcon?: DsIcon
  leading?: ReactNode
  trailing?: ReactNode
  /** Ignore a second press inside this window (Flutter default 400 ms). */
  throttle?: number
  semanticLabel?: string
}

export const DsButton = forwardRef<HTMLButtonElement, DsButtonProps>(function DsButton(
  { label, children, onClick, variant = 'primary', size = 'md', loading = false, disabled = false, fullWidth, leadingIcon, trailingIcon, leading, trailing, throttle = 400, semanticLabel, className, type = 'button', ...rest },
  ref,
) {
  const lastPress = useRef(0)
  const s = sizeClass[size]
  const iconSize = s.icon - 2
  const isDisabled = disabled || loading
  return (
    <button
      ref={ref}
      type={type}
      aria-label={semanticLabel}
      aria-busy={loading || undefined}
      disabled={isDisabled}
      onClick={(e) => {
        const now = Date.now()
        if (now - lastPress.current < throttle) return
        lastPress.current = now
        void onClick?.(e)
      }}
      className={cn(buttonVariants({ variant }), variant !== 'link' && [s.height, s.px, s.radius], s.text, fullWidth && 'w-full', className)}
      {...rest}
    >
      <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>
        {leading}
        {leadingIcon && <DsIconGlyph icon={leadingIcon} size={iconSize} />}
        {label ?? children}
        {trailingIcon && <DsIconGlyph icon={trailingIcon} size={iconSize} />}
        {trailing}
      </span>
      {loading && (
        <span className="absolute inset-0 inline-flex items-center justify-center">
          <DsSpinner size={18} inherit semanticLabel="Loading" />
        </span>
      )}
    </button>
  )
})

export interface DsIconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: DsIcon
  /** Required: it is the accessible name and the tooltip. */
  semanticLabel: string
  variant?: Extract<DsButtonVariant, 'ghost' | 'outline' | 'secondary' | 'primary'>
  size?: DsSize
  loading?: boolean
  badge?: ReactNode
}

/** Icon-only button with a tooltip; the hit area is never below 44 px. */
export const DsIconButton = forwardRef<HTMLButtonElement, DsIconButtonProps>(function DsIconButton(
  { icon, semanticLabel, variant = 'ghost', size = 'md', loading, badge, className, disabled, type = 'button', ...rest },
  ref,
) {
  const s = sizeClass[size]
  return (
    <Tooltip.Provider delayDuration={500}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            ref={ref}
            type={type}
            aria-label={semanticLabel}
            aria-busy={loading || undefined}
            disabled={disabled || loading}
            className={cn(buttonVariants({ variant }), s.height, 'w-auto min-w-tap min-h-tap rounded-lg px-0', size === 'sm' && 'min-w-9', 'aspect-square', className)}
            {...rest}
          >
            {loading ? <DsSpinner size={18} inherit /> : <DsIconGlyph icon={icon} size={s.icon} />}
            {badge && <span className="absolute right-0.5 top-0.5">{badge}</span>}
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content sideOffset={6} className="z-50 rounded-md bg-foreground px-2 py-1 text-caption text-background shadow-md animate-ds-fade-in">
            {semanticLabel}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  )
})
